import { isRejectedWithValue } from "@reduxjs/toolkit";
import type { MiddlewareAPI, Middleware } from "@reduxjs/toolkit";
import { setMessage } from "@store/snackbarSlice";
import i18n from "i18next";
import { setOpen } from "@store/offlineBannerSlice";
import { SignOutService } from "@service/sign-out.service";

let isUnauthorizedSignOutInProgress = false;

/** Normalize RTK Query / fetchBaseQuery rejection status for comparisons. Exported for unit tests. */
export function getRejectedHttpStatus(
  typedAction: Record<string, unknown> | undefined,
): number | undefined {
  if (!typedAction) {
    return undefined;
  }
  const meta = typedAction.meta as
    | { baseQueryMeta?: { response?: { status?: unknown } } }
    | undefined;
  const fromMeta = meta?.baseQueryMeta?.response?.status;
  if (typeof fromMeta === "number") {
    return fromMeta;
  }
  if (typeof fromMeta === "string" && /^\d+$/.test(fromMeta)) {
    return parseInt(fromMeta, 10);
  }

  const payload = typedAction.payload as
    | { originalStatus?: unknown; status?: unknown }
    | undefined;
  if (payload && typeof payload.originalStatus === "number") {
    return payload.originalStatus;
  }
  if (payload && typeof payload.status === "number") {
    return payload.status;
  }
  if (
    payload &&
    typeof payload.status === "string" &&
    /^\d+$/.test(payload.status)
  ) {
    return parseInt(payload.status, 10);
  }
  return undefined;
}

export const apiErrorHandler: Middleware =
  (api: MiddlewareAPI) => (next) => async (action) => {
    const typedAction = action as Record<string, unknown>;
    const responseStatus = getRejectedHttpStatus(typedAction);

    if (isRejectedWithValue(action) && responseStatus === 401) {
      const hasAuthenticatedUser = !!api.getState()?.auth?.user;

      if (hasAuthenticatedUser && !isUnauthorizedSignOutInProgress) {
        isUnauthorizedSignOutInProgress = true;
        try {
          await SignOutService.instance.signOut(api.dispatch);
        } finally {
          isUnauthorizedSignOutInProgress = false;
        }
      }
      return next(action);
    }
    if (
      isRejectedWithValue(action) &&
      (typedAction.meta as { baseQueryMeta?: { request?: { method?: string } } })
        ?.baseQueryMeta?.request?.method === "GET"
    ) {
      if (
        (typedAction.payload as { status?: string })?.status === "FETCH_ERROR" &&
        api.getState()?.auth?.user
      ) {
        api.dispatch(setOpen(true));
      } else {
        api.dispatch(
          setMessage({
            content: i18n.t("errors.unknown"),
            severity: "error",
          }),
        );
      }
    }

    return next(action);
  };
